import { type FC, useRef, useState } from 'react';
import { Show, ShowFirstMatching } from '@components/Show';
import Tooltip from '@components/Tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { ScrollArea } from '@components/ui/scroll-area';
import { CheckCircleIcon } from '@heroicons/react/solid';
import { useClosePopoverOnScrollOut } from '@hooks/use-close-popover-on-scroll-out';
import { submissionQueries } from '@queries/fetchSubmissions';
import { formatMissingDataId } from '@root/helpers/schema';
import { useQuery } from '@tanstack/react-query';

interface MissingDatapointsCountBadgeProps {
  count?: number;
  id: string;
}

export const MissingDatapointsCountBadge: FC<MissingDatapointsCountBadgeProps> = ({ count, id }) => {
  const [isPopoverOpen, setPopoverOpen] = useState<boolean>(false);
  const popoverTriggerRef = useRef<HTMLSpanElement>(null);
  useClosePopoverOnScrollOut(popoverTriggerRef, isPopoverOpen, setPopoverOpen);

  const { data: submission, isLoading } = useQuery({
    ...submissionQueries.detail(id),
    enabled: isPopoverOpen,
  });
  if (count === undefined) {
    return null;
  }
  const hasErrors = count > 0;

  if (hasErrors) {
    return (
      <Popover open={isPopoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <span ref={popoverTriggerRef} className="inline-flex">
            <Tooltip content="Missing datapoints">
              <div className="bg-warning-400 inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-full text-xs leading-4 text-white">
                {count}
              </div>
            </Tooltip>
          </span>
        </PopoverTrigger>
        <PopoverContent align="start" className="bg-gray-50 p-0">
          <div className="flex flex-col gap-2 p-4">
            <p className="mb-2 text-base leading-6 font-medium text-gray-900">Missing datapoints</p>
            <div className="mb-2 h-px w-full bg-gray-300" />
            <ScrollArea className="max-h-96 overflow-y-auto">
              <div className="pr-4">
                <ShowFirstMatching>
                  <Show when={isLoading}>
                    <div className="flex flex-col gap-2">
                      {Array.from({ length: count }).map((_, index) => (
                        <div
                          className="h-3 w-3/4 animate-pulse rounded-md bg-gray-200"
                          key={`loading-skeleton-${index}`}
                        />
                      ))}
                    </div>
                  </Show>
                  <Show when={!isLoading}>
                    {submission?.contract_missing_data?.map((error, index) => (
                      <div className="mb-2 text-xs font-medium text-gray-700 capitalize" key={`${error.id}-${index}`}>
                        {formatMissingDataId(error.id)}
                      </div>
                    ))}
                  </Show>
                </ShowFirstMatching>
              </div>
            </ScrollArea>
          </div>
        </PopoverContent>
      </Popover>
    );
  }
  return (
    <Tooltip content="Complete contract">
      <CheckCircleIcon className="text-success-400 h-6 w-6 text-xs leading-4 focus:outline-none" />
    </Tooltip>
  );
};
